import { FormattedMessage } from "react-intl";
import { Constants } from "src/billinglatest/constants";
import { formatted } from "src/billinglatest/util/currency";
import { HARDWARE_CHECKOUT_ALLOWED_COUNTRIES } from "../../shippingCountries";
import pixiDevice from "../NumberOfScreensStep/assets/pixi-device.svg";
import {
  BillingCycle,
  CurrencyCode,
  DealId,
  DISCOUNT_PERCENT,
  PER_DEVICE_VALUE_CENTS,
  dealEligibility,
} from "./constants";
import {
  CountrySelect,
  DealCardStyled,
  DealContent,
  DealFootnote,
  DealPriceRow,
  DealSavings,
  DealTitle,
  DealsColumn,
  DealsEmpty,
  DealsHeader,
  DealsList,
  DealsSubtitle,
  DealsTitle,
  DeviceCount,
  DeviceImage,
  DiscountPercent,
  DiscountValue,
  DiscountWord,
  FootnoteGroup,
  GlobeIcon,
  SaveAmount,
  ShipToLabel,
  ShipToRow,
} from "./styles";

export interface ExclusiveDealsProps {
  billingCycle: BillingCycle;
  currency: CurrencyCode;
  licenses: number;
  selectedDealId?: DealId;
  shippingCountry: string;
  onSelectDeal: (dealId: DealId) => void;
  onChangeShippingCountry: (value: string) => void;
}

export const ExclusiveDeals = ({
  billingCycle,
  currency,
  licenses,
  selectedDealId,
  shippingCountry,
  onSelectDeal,
  onChangeShippingCountry,
}: ExclusiveDealsProps) => {
  const eligibility = dealEligibility(billingCycle);
  const savings = formatted(
    currency,
    PER_DEVICE_VALUE_CENTS[currency] * licenses,
  );

  const deviceSavings = (
    <DealSavings>
      <SaveAmount>
        <FormattedMessage
          id="pixi_wizard.plan.deals.save"
          defaultMessage="Save {amount}"
          values={{ amount: savings }}
        />
      </SaveAmount>
      <DeviceCount>
        <FormattedMessage
          id="pixi_wizard.plan.deals.devices"
          defaultMessage="x{count} Devices"
          values={{ count: licenses }}
        />
      </DeviceCount>
    </DealSavings>
  );

  return (
    <DealsColumn data-testid="exclusive-deals">
      <DealsHeader>
        <DealsTitle>
          <FormattedMessage
            id="pixi_wizard.plan.deals.title"
            defaultMessage="Exclusive deals"
          />
        </DealsTitle>
        <DealsSubtitle>
          <FormattedMessage
            id="pixi_wizard.plan.deals.subtitle"
            defaultMessage="Depending on the plan you choose, you'll get access to exclusive one-time deals."
          />
        </DealsSubtitle>
      </DealsHeader>

      {!eligibility.anyDeal ? (
        <DealsEmpty data-testid="deals-empty">
          <FormattedMessage
            id="pixi_wizard.plan.deals.none"
            defaultMessage="Unfortunately, there are no deals available on this plan"
          />
        </DealsEmpty>
      ) : (
        <DealsList>
          {eligibility.freeDeviceAvailable && (
            <DealCardStyled
              selected={selectedDealId === "free-device"}
              onClick={() => onSelectDeal("free-device")}
              data-testid="deal-free-device"
            >
              <DealContent>
                <DealTitle>
                  <FormattedMessage
                    id="pixi_wizard.plan.deals.free_devices"
                    defaultMessage="I want free devices!"
                  />
                </DealTitle>
                <DealPriceRow>
                  <DeviceImage src={pixiDevice} alt="" aria-hidden />
                  {deviceSavings}
                </DealPriceRow>
                <FootnoteGroup>
                  <DealFootnote>
                    <FormattedMessage
                      id="pixi_wizard.plan.deals.per_license"
                      defaultMessage="1 Free device for every licenses"
                    />
                  </DealFootnote>
                  <ShipToRow>
                    <ShipToLabel>
                      <FormattedMessage
                        id="pixi_wizard.plan.deals.ship_to"
                        defaultMessage="Ship to"
                      />
                    </ShipToLabel>
                    <GlobeIcon name="globe" aria-hidden />
                    <CountrySelect
                      data-testid="shipping-country-select"
                      aria-label="Shipping country"
                      value={shippingCountry}
                      onClick={(event) => event.stopPropagation()}
                      onChange={(event) =>
                        onChangeShippingCountry(event.target.value)
                      }
                    >
                      {HARDWARE_CHECKOUT_ALLOWED_COUNTRIES.map((code) => (
                        <option key={code} value={code}>
                          {Constants.Countries[code]}
                        </option>
                      ))}
                    </CountrySelect>
                  </ShipToRow>
                </FootnoteGroup>
              </DealContent>
            </DealCardStyled>
          )}

          {eligibility.discountAvailable && (
            <DealCardStyled
              selected={selectedDealId === "discount"}
              onClick={() => onSelectDeal("discount")}
              data-testid="deal-discount"
            >
              <DealContent>
                <DealTitle>
                  <FormattedMessage
                    id="pixi_wizard.plan.deals.discount"
                    defaultMessage="I want a discount"
                  />
                </DealTitle>
                <DiscountValue>
                  <DiscountPercent data-testid="discount-percent">
                    {DISCOUNT_PERCENT}%
                  </DiscountPercent>{" "}
                  <DiscountWord>
                    <FormattedMessage
                      id="pixi_wizard.plan.deals.discount_word"
                      defaultMessage="Discount"
                    />
                  </DiscountWord>
                </DiscountValue>
              </DealContent>
            </DealCardStyled>
          )}
        </DealsList>
      )}
    </DealsColumn>
  );
};
